import axios, { AxiosProgressEvent } from "axios";
import { CSSProperties, useCallback, useEffect } from "react";
import { myAxios } from "src/components/common/functions";
import { useUploadStore } from "src/components/main/uploadStore";

type UploadStatus = -2 | -1 | 1 | number;

type StyleMap = {
  overlay: CSSProperties;
  modal: CSSProperties;
  progressBarOuter: CSSProperties;
  progressBarInner: CSSProperties;
};

const styles: StyleMap = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999
  },
  modal: {
    background: "#fff",
    padding: 20,
    borderRadius: 8,
    width: 350,
    maxHeight: "80vh",
    overflowY: "auto",
    boxShadow: "0 5px 20px rgba(0,0,0,0.2)"
  },
  progressBarOuter: {
    width: "100%",
    height: 8,
    background: "#eee",
    borderRadius: 4,
    overflow: "hidden"
  },
  progressBarInner: {
    height: "100%",
    background: "#4caf50",
    transition: "width 0.2s ease"
  }
};

export default function UploadProgressModal() {
  const {
    show,
    files,
    urls,
    ids,
    fileUploadProgress,
    totalProgress,
    setFileUploadProgress,
    reset
  } = useUploadStore();

  const startUpload = useCallback(async () => {
    async function uploadSingle(file: File, url: string, id: string, index: number): Promise<boolean> {
      const MAX_RETRY = 3;
      let attempt = 0;
      while (attempt < MAX_RETRY) {
        try {
          await axios.put(url, file, {
            headers: { "Content-Type": file.type },
            onUploadProgress: (e: AxiosProgressEvent) => {
              const total = e.total ?? file.size;
              const p = total > 0 ? e.loaded / total : 0;
              setFileUploadProgress(index, p);
            }
          });

          const formData = new FormData();
          formData.append("fileId", id);
          await myAxios({ fileName: "fileCompleted.php", formData });
          return true;
        } catch (e) {
          attempt += 1;
          console.warn(`Upload failed (try ${attempt}), reason:`, e);

          if (!navigator.onLine) {
            setFileUploadProgress(index, -2);
            return false;
          }

          if (attempt >= MAX_RETRY) {
            setFileUploadProgress(index, -1);
            return false;
          }

          const delay = attempt * 500;
          await new Promise<void>((resolve) => {
            window.setTimeout(resolve, delay);
          });
        }
      }

      return false;
    }

    const concurrency = 3;
    let index = 0;

    async function worker(): Promise<void> {
      while (index < files.length) {
        const i = index;
        index += 1;
        await uploadSingle(files[i], urls[i], ids[i], i);
      }
    }

    await Promise.all(
      Array(Math.min(concurrency, files.length))
        .fill(0)
        .map(() => worker())
    );

    window.setTimeout(() => reset(), 5000);
  }, [files, urls, ids, setFileUploadProgress, reset]);

  useEffect(() => {
    if (show) {
      if (files.length) {
        void startUpload();
      } else {
        reset();
      }
    }
  }, [show, startUpload, files.length, reset]);

  if (!show) {
    return null;
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h3>Uploading...</h3>
        <div>Total Progress: {(totalProgress * 100).toFixed(1)}%</div>
        <div style={styles.progressBarOuter}>
          <div
            style={{
              ...styles.progressBarInner,
              width: `${totalProgress * 100}%`
            }}
          />
        </div>

        {files.map((file, i) => (
          <div key={i}>
            <div>{file.name}</div>
            <div style={styles.progressBarOuter}>
              <div
                style={{
                  ...styles.progressBarInner,
                  width: `${(fileUploadProgress[i] || 0) * 100}%`
                }}
              />
            </div>
            <div style={{ fontSize: 12, marginTop: 4 }}>{renderStatus(fileUploadProgress[i])}</div>
          </div>
        ))}

        <button className="mt-3 btn btn-secondary" onClick={reset}>
          Close
        </button>
      </div>
    </div>
  );
}

function renderStatus(progress: UploadStatus | undefined): string {
  switch (progress) {
    case -2:
      return "📵 Offline, waiting for reconnect";
    case 1:
      return "✔ Completed";
    case -1:
      return "❌ Upload failed";
    default:
      return "⬆ Uploading...";
  }
}
